import { uniqBy } from "lodash"
import { buildCampaignAnalysisCacheKey } from "helpers/sf_utils"
import {
  GET_ALL_PROSPECTS,
  GET_ALL_PROSPECTS_SUCCESS,
  GET_ALL_PROSPECTS_ERROR,
  UPDATE_CPA_DISPOSITION,
  UPDATE_CPA_DISPOSITION_SUCCESS,
  UPDATE_CPA_DISPOSITION_ERROR,
  BULK_EDIT_CPA_DISPOSITION,
  BULK_EDIT_CPA_DISPOSITION_SUCCESS,
  BULK_EDIT_CPA_DISPOSITION_ERROR,
  GET_CPA_BULK_EDIT_STATUS,
  GET_CPA_BULK_EDIT_STATUS_SUCCESS,
  GET_CPA_BULK_EDIT_STATUS_ERROR,
  PREVIEW_CPA_BULK_EDIT,
  PREVIEW_CPA_BULK_EDIT_SUCCESS,
  PREVIEW_CPA_BULK_EDIT_ERROR,
  RESET_CPA_BULK_EDIT_PREVIEW,
} from "./actionTypes"

const parsePositiveInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

const INIT_STATE = {
  all_prospects: [],
  additional_data: null,
  error: null,
  loading: false,
  update_loading: false,
  // Number of disposition updates currently in flight. The saga handles
  // UPDATE_CPA_DISPOSITION with takeEvery, so several can overlap; counting
  // them keeps update_loading true until the last one settles.
  pending_updates: 0,
  last_page_fetched: 0,
  last_fetched_at: null,
  last_start_date: null,
  last_end_date: null,
  has_next_page: false,
  total_number_of_pages: 0,
  has_lookahead_pages: false,
  lookahead_pages: 0,
  active_cache_key: null,
  page_cache: {},
  bulk_edit_job: null,
  bulk_edit_loading: false,
  bulk_edit_status_loading: false,
  bulk_edit_error: null,
  bulk_edit_preview: { loading: false, count: null, lockHeld: false, error: null },
}

const AllProspects = (state = INIT_STATE, action) => {
  switch (action.type) {
    case GET_ALL_PROSPECTS: {
      const queryParams = action.payload?.queryParams || {}
      const pageNumber = Math.max(1, Number(queryParams.page_number) || 1)
      const appendResults = action.payload?.appendResults === true
      const cacheKey = buildCampaignAnalysisCacheKey(queryParams)
      const hasBaseQueryChanged =
        Boolean(cacheKey) && cacheKey !== state.active_cache_key
      // Reset the list for a fresh query (page 1) or any non-append fetch
      // (offset page change); keep it only when appending the next page.
      return {
        ...state,
        error: null,
        loading: true,
        all_prospects:
          pageNumber === 1 || !appendResults || hasBaseQueryChanged
            ? []
            : state.all_prospects,
        last_page_fetched: hasBaseQueryChanged ? 0 : state.last_page_fetched,
        total_number_of_pages: hasBaseQueryChanged
          ? 0
          : state.total_number_of_pages,
        has_next_page: hasBaseQueryChanged ? false : state.has_next_page,
        has_lookahead_pages: hasBaseQueryChanged
          ? false
          : state.has_lookahead_pages,
        lookahead_pages: hasBaseQueryChanged ? 0 : state.lookahead_pages,
        active_cache_key: cacheKey || state.active_cache_key,
        page_cache: hasBaseQueryChanged ? {} : state.page_cache,
      }
    }

    case GET_ALL_PROSPECTS_SUCCESS: {
      const additional = action.payload?.additional_data || {}
      const appendResults = action.meta?.appendResults === true
      const merged = appendResults
        ? [...state.all_prospects, ...(action.payload?.data || [])]
        : [...(action.payload?.data || [])]
      const hasLookaheadPages = Boolean(additional.has_lookahead_pages)
      const currentPage = parsePositiveInt(
        additional.page_number,
        action.meta?.pageNumber || state.last_page_fetched
      )
      const nextPageCache = action.meta?.cacheKey
        ? {
            ...(action.meta?.invalidateCache ? {} : state.page_cache),
            [action.meta.cacheKey]: {
              ...((action.meta?.invalidateCache ? {} : state.page_cache)?.[
                action.meta.cacheKey
              ] || {}),
              [currentPage]: action.payload,
            },
          }
        : state.page_cache
      return {
        ...state,
        error: null,
        loading: false,
        last_fetched_at: new Date(),
        all_prospects: uniqBy(merged, "id"),
        additional_data: action.payload?.additional_data,
        last_page_fetched: currentPage,
        total_number_of_pages: parsePositiveInt(
          additional.total_number_of_pages,
          0
        ),
        has_next_page: Boolean(additional.has_next_page),
        has_lookahead_pages: hasLookaheadPages,
        lookahead_pages: hasLookaheadPages
          ? parsePositiveInt(additional.lookahead_pages, 8)
          : 0,
        last_start_date: additional.start_date,
        last_end_date: additional.end_date,
        active_cache_key: action.meta?.cacheKey || state.active_cache_key,
        page_cache: nextPageCache,
      }
    }

    case GET_ALL_PROSPECTS_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
        has_next_page: false,
        has_lookahead_pages: false,
        lookahead_pages: 0,
      }

    case UPDATE_CPA_DISPOSITION:
      return {
        ...state,
        error: null,
        pending_updates: state.pending_updates + 1,
        update_loading: true,
      }

    case UPDATE_CPA_DISPOSITION_SUCCESS: {
      const pending_updates = Math.max(0, state.pending_updates - 1)
      return {
        ...state,
        error: null,
        pending_updates,
        update_loading: pending_updates > 0,
        all_prospects: state.all_prospects.map(prospect => {
          const updated = (action.payload || []).find(
            el => el.id === prospect.id
          )
          return updated ? updated : prospect
        }),
        page_cache: {},
      }
    }

    case UPDATE_CPA_DISPOSITION_ERROR: {
      const pending_updates = Math.max(0, state.pending_updates - 1)
      return {
        ...state,
        error: action.payload,
        pending_updates,
        update_loading: pending_updates > 0,
      }
    }

    case BULK_EDIT_CPA_DISPOSITION:
      return { ...state, bulk_edit_error: null, bulk_edit_loading: true }

    case BULK_EDIT_CPA_DISPOSITION_SUCCESS:
      return { ...state, bulk_edit_job: action.payload, bulk_edit_loading: false }

    case BULK_EDIT_CPA_DISPOSITION_ERROR:
      return { ...state, bulk_edit_error: action.payload, bulk_edit_loading: false }

    case GET_CPA_BULK_EDIT_STATUS:
      return { ...state, bulk_edit_status_loading: true }

    case GET_CPA_BULK_EDIT_STATUS_SUCCESS:
      return { ...state, bulk_edit_job: action.payload || null, bulk_edit_status_loading: false }

    case GET_CPA_BULK_EDIT_STATUS_ERROR:
      return { ...state, bulk_edit_error: action.payload, bulk_edit_status_loading: false }

    case PREVIEW_CPA_BULK_EDIT:
      return {
        ...state,
        bulk_edit_preview: { loading: true, count: null, lockHeld: false, error: null },
      }

    case PREVIEW_CPA_BULK_EDIT_SUCCESS:
      return {
        ...state,
        bulk_edit_preview: {
          loading: false,
          count: typeof action.payload?.count === "number" ? action.payload.count : null,
          lockHeld: Boolean(action.payload?.lock_held),
          error: null,
        },
      }

    case PREVIEW_CPA_BULK_EDIT_ERROR:
      return {
        ...state,
        bulk_edit_preview: { loading: false, count: null, lockHeld: false, error: action.payload },
      }

    case RESET_CPA_BULK_EDIT_PREVIEW:
      return { ...state, bulk_edit_preview: INIT_STATE.bulk_edit_preview }

    default:
      return state
  }
}

export default AllProspects
